import { Link, useLocation } from 'react-router-dom';
import { DukantiBrandMark, useTranslation } from '@dukanti/shared';
import { LogOut, Settings } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import { OrdersNavBadge } from '@/features/orders';
import { useStore } from '@/features/store-settings/use-store';
import { useBillingPermissions } from '@/hooks/use-billing-permissions';
import { useCourierPermissions } from '@/hooks/use-courier-permissions';
import { useNewsletterSubscriberPermissions } from '@/hooks/use-newsletter-subscriber-permissions';
import { AdminSignOutDialog } from './admin-sign-out-dialog';
import { dirToSide, isActive, useNavItems } from './admin-shell-nav-items';

interface AdminShellSidebarProps {
  storeId: string;
  userId: string | null;
  userEmail: string | null;
}

/**
 * Admin sidebar — iterates useNavItems() (hydrated via i18n) and renders
 * one <SidebarMenuItem> per entry. Active state is derived from
 * useLocation().pathname via isActive() — no local state.
 *
 * RTL: the `side` prop is driven by `dirToSide(dir)` per F-027 §5.3 C2.
 * shadcn's Sidebar emits physical left-0/right-0 classes keyed on this
 * prop and does NOT auto-flip from dir="rtl"; we pass side explicitly so
 * the sidebar lands on the content-start edge under both directions.
 *
 * Mobile auto-close: on mobile viewports the sidebar renders via a
 * `<Sheet>` offcanvas. Clicking a nav link navigates via React Router,
 * but shadcn does NOT auto-close the sheet — the canonical shadcn pattern
 * (see ui.shadcn.com/docs/components/sidebar) is to call
 * `useSidebar().setOpenMobile(false)` in the link's onClick so the
 * offcanvas closes on the same tap that triggers navigation. Without this,
 * the sheet traps focus and blocks subsequent interactions in Playwright.
 */
export function AdminShellSidebar({ storeId, userId, userEmail }: AdminShellSidebarProps) {
  const { t, dir } = useTranslation();
  const { pathname } = useLocation();
  const side = dirToSide(dir);
  const { isMobile, setOpenMobile } = useSidebar();
  const store = useStore(storeId);
  const courierPermissions = useCourierPermissions(storeId);
  const billingPermissions = useBillingPermissions(storeId);
  const subscriberPermissions = useNewsletterSubscriberPermissions(storeId);
  const items = useNavItems({
    canManageCouriers: courierPermissions.data?.canManageCouriers === true,
    canManageBilling: billingPermissions.data?.canManageBilling === true,
    canManageNewsletterSubscribers:
      !subscriberPermissions.isError &&
      subscriberPermissions.data?.canManageNewsletterSubscribers === true,
  });

  const storeName = store.data?.name ?? t('admin.nav.storeFallback');
  const storeSlug = store.data?.slug ?? null;
  const logoUrl = store.data?.logo_url ?? null;
  const storeInitial = storeName.trim().charAt(0).toUpperCase() || 'D';
  const accountLabel = userEmail ?? t('admin.nav.accountFallback');

  const handleNavClick = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <Sidebar
      side={side}
      variant="inset"
      collapsible="offcanvas"
      data-testid="admin-shell-sidebar"
      aria-label={t('admin.nav.ariaLabel')}
    >
      <SidebarHeader className="gap-3 p-3">
        <div
          data-testid="admin-shell-sidebar-brand"
          className="flex items-center gap-3 rounded-lg px-2 py-1 text-start"
        >
          <DukantiBrandMark className="size-9 rounded-md shadow-sm" />
          <div className="min-w-0">
            <p className="font-display text-sidebar-foreground truncate text-base font-semibold">
              Dukanti
            </p>
            <p className="text-sidebar-foreground/65 truncate text-xs">
              {t('admin.nav.storeWorkspace')}
            </p>
          </div>
        </div>
        <div
          data-testid="admin-shell-sidebar-store"
          className="border-sidebar-border/70 bg-sidebar-accent/45 flex items-center gap-3 rounded-lg border p-2 text-start"
        >
          <div className="bg-sidebar-primary/20 text-sidebar-foreground flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md text-sm font-semibold">
            {logoUrl ? (
              <img src={logoUrl} alt={storeName} className="size-full object-cover" />
            ) : (
              storeInitial
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sidebar-foreground truncate text-sm font-medium">{storeName}</p>
            {storeSlug ? (
              <p className="text-sidebar-foreground/60 truncate text-xs">{storeSlug}</p>
            ) : (
              <p className="text-sidebar-foreground/60 truncate text-xs">
                {t('admin.nav.storeWorkspace')}
              </p>
            )}
          </div>
        </div>
      </SidebarHeader>
      <SidebarSeparator />
      <SidebarContent className="px-2 py-3">
        <SidebarMenu>
          {items.map((item) => {
            const contract = item.contract;
            const active = isActive(contract, pathname);
            return (
              <SidebarMenuItem
                key={item.to}
                {...(contract.to === '/orders' ? { 'data-testid': 'orders-nav-link' } : {})}
              >
                <SidebarMenuButton
                  asChild
                  isActive={active}
                  data-testid={`admin-shell-sidebar-link-${item.to.replace(/\//g, '-') || 'root'}`}
                >
                  <Link
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    onClick={handleNavClick}
                  >
                    <item.Icon className="size-4" aria-hidden="true" />
                    <span>{item.label}</span>
                    {contract.to === '/orders' && <OrdersNavBadge userId={userId} />}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarContent>
      <SidebarSeparator />
      <SidebarFooter className="p-3">
        <div
          data-testid="admin-shell-sidebar-account"
          className="border-sidebar-border/70 bg-sidebar-accent/45 rounded-lg border p-2 text-start"
        >
          <p className="text-sidebar-foreground truncate text-sm font-medium">{accountLabel}</p>
          <div className="mt-3 grid gap-1">
            <Link
              to="/dashboard/settings"
              onClick={handleNavClick}
              className="text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-sidebar-ring flex min-h-8 w-full items-center gap-2 rounded-md px-2 text-xs font-medium outline-none transition-colors focus-visible:ring-2"
              aria-label={t('admin.nav.storeSettings')}
            >
              <Settings className="size-3.5" aria-hidden="true" />
              <span className="truncate">{t('admin.nav.storeSettings')}</span>
            </Link>
            <AdminSignOutDialog
              triggerTestId="admin-shell-sign-out"
              triggerClassName="text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-sidebar-ring mt-3 flex min-h-9 w-full items-center gap-2 rounded-md border-t border-sidebar-border/70 px-2 pt-3 text-xs font-medium outline-none transition-colors focus-visible:ring-2"
              onConfirmStart={() => {
                if (isMobile) setOpenMobile(false);
              }}
            >
              <LogOut className="size-3.5" aria-hidden="true" />
              <span className="truncate">{t('admin.nav.signOut')}</span>
            </AdminSignOutDialog>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
